import { useCallback, useEffect, useState } from 'react';

import LoadingSpinner from 'components/core/LoadingSpinner/LoadingSpinner';
import Wrapper from 'components/layouts/Wrapper/Wrapper';
import ListEmpty from 'components/modules/Common/ListEmpty/ListEmpty';
import {
  useGetNotOnboardedVendorsQuery,
  useGetVendorsQuery,
  useInviteVendorsMutation,
} from '../../../../services/vendorsApi';
import { isPermitted, useGetUserQuery } from '../../../../services/userApi';
import Headers from '../../../core/Headers/Headers';
import { handleVndorsTabNavData } from 'constants/TabNavData';
import { Message, ToolBar } from 'components/core';
import Icon from 'components/core/Media/Icon/Icon';
import DataList from 'components/core/SuperTable/DataList/DataList';
import { useServerSidePagination } from 'hooks/useServerSidePagination';
import { helperNotOnboardedVendorColumns } from 'helpers/modules/helperVendor';
import { useShowOptionHandler } from 'hooks/useShowOptionHandler';
import {
  InviteVendorItemResult,
  InviteVendorsResponse,
  NotOnboardedVendor,
} from 'shared/models/services/vendors.model';
import OnboardManagedModal from './OnboardManagedModal';

/**
 * Not-onboarded vendors tab.
 *
 * Data is CoupaSupplier-anchored (POST /coupa/not-onboarded-vendors) and is
 * always served server-side: pagination AND search are query params. Rows are
 * NOT links (no Account detail page exists for these vendors) and there is no
 * VendorsStatsBar (payout stats do not apply).
 */
const NotOnboardedVendorsContainer = () => {
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [filteredRow, setFilteredRow] = useState([]);
  const [tableColumns, handleIconClick] = helperNotOnboardedVendorColumns();

  // Invite mutation
  const [inviteVendors, { isLoading: isInviting }] = useInviteVendorsMutation();

  // Inline feedback (no toast library in this app — follows NotificationForm pattern)
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteMessageType, setInviteMessageType] = useState<'success' | 'error'>('success');

  // Bulk selection
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  // Current user for role gating
  const { data: user } = useGetUserQuery({});
  const isAdmin = !!isPermitted(user, 'edit-vendor');
  const isSuperAdmin = !!isPermitted(user, 'onboard-vendor');

  // Managed onboarding modal (super-admin self-service)
  const [onboardTarget, setOnboardTarget] = useState<NotOnboardedVendor | null>(null);

  // Search input (raw) + debounced value applied to the query.
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [
    pageSizeOptions,
    paginationState,
    previousButtonClick,
    nextButtonClick,
    setPageSize,
    resetPageIndex,
  ]: any = useServerSidePagination();

  // Debounce the search term so we don't fire a request on every keystroke.
  // Reset to the first page on every new search, otherwise a stale offset from
  // a previous page lands past the (smaller) result set and renders empty.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      resetPageIndex();
    }, 400);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const notOnboardedParams = {
    first: paginationState.pageSize,
    offset: paginationState.pageIndex * paginationState.pageSize,
    search,
  };

  // Stable callbacks: these are dependencies of DataList's columns useMemo. If they
  // were recreated each render (inline arrows), the columns array would rebuild every
  // render, react-table would emit a new visibleColumns every render, and SuperTable's
  // setVisibleColumns lift would churn parent state into an infinite loop (React #185).
  const handleInvite = useCallback(
    async (identityKeys: string[]) => {
      const resp: InviteVendorsResponse | null = await inviteVendors({ identityKeys })
        .unwrap()
        .catch(() => null);
      if (!resp) {
        setInviteMessageType('error');
        setInviteMessage('Failed to send invites. Please try again.');
        setTimeout(() => setInviteMessage(''), 6500);
        return;
      }
      const invited = resp.results.filter(
        (r: InviteVendorItemResult) => r.status === 'invited'
      ).length;
      const skipped = resp.results.length - invited;
      setInviteMessageType('success');
      setInviteMessage(
        skipped
          ? `Invited ${invited}, skipped ${skipped} with no contact info`
          : `Invited ${invited} vendor(s)`
      );
      setSelectedKeys([]);
      setTimeout(() => setInviteMessage(''), 6500);
    },
    [inviteVendors]
  );

  const handleInviteRow = useCallback((key: string) => handleInvite([key]), [handleInvite]);

  const handleSelectKey = useCallback((key: string, checked: boolean) => {
    setSelectedKeys((prev) => (checked ? [...prev, key] : prev.filter((k) => k !== key)));
  }, []);

  // Stable (see the React #185 note above): opens the managed-onboarding modal for a row.
  const handleCompleteOnboarding = useCallback((vendor: NotOnboardedVendor) => {
    setOnboardTarget(vendor);
  }, []);

  const handleOnboardSuccess = useCallback(() => {
    const name = onboardTarget?.name ?? 'Vendor';
    setOnboardTarget(null);
    setInviteMessageType('success');
    setInviteMessage(`${name} onboarded. It can now be paid.`);
    setTimeout(() => setInviteMessage(''), 6500);
  }, [onboardTarget]);

  /**
   * handle data request from redux_toolkit
   */
  const { data, isLoading, isFetching, isSuccess, isError, error }: any =
    useGetNotOnboardedVendorsQuery(notOnboardedParams);

  const vendors: any = isSuccess && data ? data?.vendors ?? [] : [];
  const totalRecords: number = isSuccess && data ? data?.totalCount ?? 0 : 0;
  const totalPageCount = Math.ceil(totalRecords / paginationState.pageSize);

  // The Active / Inactive tab counts come from the accounts aggregates
  // (allActiveAccounts / allInactiveAccounts), which live on the /query/accounts
  // response, NOT on the not-onboarded payload. Fetch them with a minimal call so
  // this tab's nav shows the same counts as the Active/Inactive tabs. The
  // aggregates are independent of the filter/first, so we request a single row.
  const accountCountParams: any = {
    first: 1,
    offset: 0,
    filter: { status: { equalTo: 'ACTIVE' } },
  };
  const { data: accountsData }: any = useGetVendorsQuery(accountCountParams);

  const [vendorsTabnavData] = handleVndorsTabNavData(accountsData, totalRecords);

  const [
    hiddenColumns,
    toggleHiddenColumnChanged,
    setToggleHiddenColumnChanged,
    columnList,
    setColumnList,
    vendorNodes,
  ] = useShowOptionHandler({
    tableColumns: tableColumns,
    data: vendors,
    additionalColumns: [],
    storageItemName: 'showOptionNotOnboardedVendorList',
  });

  const isSearchActive = search.length > 0;

  return (
    <Wrapper additionalClasses="flex-col items-start" pageTitle="Vendors">
      {/**
       * Title section
       */}
      <Headers headingContent="Vendors" />
      {/**
       * Tab navigation + server-side search box
       */}
      <ToolBar
        navigationData={vendorsTabnavData}
        isShowOptionEnabled={isSuccess && vendorNodes && vendorNodes.length > 0}
        isCountVisible
        visibleColumns={visibleColumns}
        filteredRow={filteredRow}
        isDownloadCsvButtonEnabled={false}
        columns={columnList}
        handleIconClick={(item: any) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          handleIconClick(
            item,
            columnList,
            setColumnList,
            setToggleHiddenColumnChanged,
            toggleHiddenColumnChanged
          );
        }}
        tabType="vendor"
      />
      {/**
       * Server-side search box + bulk invite button row.
       */}
      <div className="flex items-center gap-3 mb-3">
        <div className="flex items-center w-[280px] h-9 rounded-lg border border-grey-300 bg-white px-2">
          <Icon iconType="SearchIcon" additionalClasses="mr-1 text-grey-600" />
          <input
            type="text"
            placeholder="Search vendors"
            value={searchInput}
            className="h-8 w-full bg-white placeholder:text-grey-600 text-12s font-light rounded-lg outline-none"
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        {isAdmin && selectedKeys.length > 0 && (
          <button
            type="button"
            disabled={isInviting}
            onClick={() => handleInvite(selectedKeys)}
            className="flex items-center justify-center px-3 h-9 rounded-lg text-14s text-white bg-primary-500 border border-primary-500 disabled:opacity-[0.38] disabled:pointer-events-none"
          >
            Invite selected ({selectedKeys.length})
          </button>
        )}
      </div>
      {inviteMessage && (
        <Message type={inviteMessageType} additionalClasses="mb-3">
          {inviteMessage}
        </Message>
      )}
      {/**
       * Handle loader
       */}
      {isLoading && <LoadingSpinner />}
      {/**
       * Handle api request error
       */}
      {isError && (
        <Message type="error" title={`Not onboarded vendors`}>
          {error?.error ?? error?.data?.message ?? error?.data?.error}
        </Message>
      )}

      {isSuccess && (
        <>
          {vendorNodes && vendorNodes.length > 0 ? (
            <DataList
              data={vendorNodes}
              editNetTerm={false}
              isLink={false}
              tableType="notOnboarded"
              isPagination
              subTabType="notOnboardedVendors"
              isServerSidePagination
              isDataFetching={isFetching}
              previousPageClick={previousButtonClick}
              nextPageClick={nextButtonClick}
              setPageSizeValue={setPageSize}
              options={pageSizeOptions}
              totalPageCount={totalPageCount}
              pageSizeVal={paginationState.pageSize}
              pageIndexVal={paginationState.pageIndex}
              setVisibleColumns={setVisibleColumns}
              setFilteredRow={setFilteredRow}
              hiddenColumnList={hiddenColumns}
              toggleHiddenColumnChanged={toggleHiddenColumnChanged}
              onInvite={handleInviteRow}
              isAdmin={isAdmin}
              selectedKeys={selectedKeys}
              onSelectKey={handleSelectKey}
              onCompleteOnboarding={handleCompleteOnboarding}
              canOnboard={isSuperAdmin}
            />
          ) : isSearchActive ? (
            <div className="flex flex-col items-center justify-center w-full py-16 text-grey-600">
              <Icon iconType="SearchIcon" additionalClasses="mb-2 text-grey-600" />
              <span className="text-14s">No vendors match “{search}”.</span>
            </div>
          ) : (
            <ListEmpty />
          )}
        </>
      )}

      <OnboardManagedModal
        isOpen={!!onboardTarget}
        vendor={onboardTarget}
        onClose={() => setOnboardTarget(null)}
        onSuccess={handleOnboardSuccess}
      />
    </Wrapper>
  );
};

export default NotOnboardedVendorsContainer;
